import React, { useEffect, useMemo, useState } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Input, Btn, Note, Modal, Sel } from "./ui";
import { Icon } from "./Icons";
import { supabase, isSupabaseReady } from "./supabase";
import { ModalAtendido, ModalPerdido, ModalFechamento, ModalProjeto, criarPedidoWix, criarVendaPendente } from "./PlantaoModais";

// ═══════════════════════════════════════════════════════════════════════
// PLANTÃO — Admin gerencia rodízio WhatsApp
// Adicionar / editar / ativar / horários / histórico de leads
// ═══════════════════════════════════════════════════════════════════════

const DIAS_SEMANA = [
  { id: 0, label: "Dom", curto: "D" },
  { id: 1, label: "Seg", curto: "S" },
  { id: 2, label: "Ter", curto: "T" },
  { id: 3, label: "Qua", curto: "Q" },
  { id: 4, label: "Qui", curto: "Q" },
  { id: 5, label: "Sex", curto: "S" },
  { id: 6, label: "Sáb", curto: "S" },
];

const HORARIO_COMERCIAL = [
  { dia: 1, inicio: "08:00", fim: "18:00" },
  { dia: 2, inicio: "08:00", fim: "18:00" },
  { dia: 3, inicio: "08:00", fim: "18:00" },
  { dia: 4, inicio: "08:00", fim: "18:00" },
  { dia: 5, inicio: "08:00", fim: "18:00" },
  { dia: 6, inicio: "09:00", fim: "13:00" },
];

function formatarTelefone(t) {
  if (!t) return "";
  const s = String(t).replace(/\D/g, "");
  if (s.length >= 12) {
    // 55 85 99999-9999
    return `+${s.slice(0, 2)} ${s.slice(2, 4)} ${s.slice(4, 9)}-${s.slice(9)}`;
  }
  return t;
}

function limparTelefone(t) {
  if (!t) return "";
  const s = String(t).replace(/\D/g, "");
  if (s.length === 10 || s.length === 11) return "55" + s;
  return s;
}

export default function PlantaoAdmin({ currentUser, corretores: corretoresCRM }) {
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "admin_master" || currentUser?.role === "proprietario" || currentUser?.role === "comercial";
  const [corretores, setCorretores] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [novo, setNovo] = useState(false);
  const [novoLead, setNovoLead] = useState(false);
  const [mAtendido, setMAtendido] = useState(null);
  const [mPerdido, setMPerdido] = useState(null);
  const [mFechar, setMFechar] = useState(null);
  const [mProjeto, setMProjeto] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState("leads"); // leads | corretores
  // Filtros aba leads
  const [fCorretor, setFCorretor] = useState("todos");
  const [fStatus, setFStatus]     = useState("todos");
  const [fCampanha, setFCampanha] = useState("todos");
  const [fPeriodo, setFPeriodo]   = useState("todos");
  const [fDataDe, setFDataDe]     = useState("");
  const [fDataAte, setFDataAte]   = useState("");
  const [fBusca, setFBusca]       = useState("");

  async function carregar() {
    if (!isSupabaseReady) return;
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        supabase.from("plantao_corretores").select("*").order("ordem", { ascending: true }),
        supabase.from("plantao_leads").select("*").order("created_at", { ascending: false }).limit(1000),
      ]);
      setCorretores(c.data || []);
      setLeads(l.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  async function salvar(c) {
    if (!isSupabaseReady) return;
    try {
      const payload = {
        nome: c.nome.trim(),
        telefone: limparTelefone(c.telefone),
        ativo: !!c.ativo,
        horarios: c.horarios || [],
        ordem: c.ordem || 0,
        max_leads_dia: c.max_leads_dia === "" || c.max_leads_dia == null ? null : parseInt(c.max_leads_dia, 10),
        corretor_id: c.corretor_id || null,
        observacoes: c.observacoes || null,
      };
      if (c.id) {
        const { error } = await supabase.from("plantao_corretores").update(payload).eq("id", c.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("plantao_corretores").insert(payload);
        if (error) throw error;
      }
      setEditando(null); setNovo(false);
      carregar();
    } catch (e) {
      alert("Erro ao salvar: " + e.message);
    }
  }

  async function salvarLead(d) {
    if (!isSupabaseReady) return;
    const cor = corretores.find(c => c.id === d.plantao_corretor_id);
    try {
      const { error } = await supabase.from("plantao_leads").insert({
        plantao_corretor_id: d.plantao_corretor_id || null,
        nome_corretor: cor?.nome || null,
        telefone_corretor: cor?.telefone || null,
        nome_lead: [d.nome_lead, d.sobrenome_lead].map(s => (s || "").trim()).filter(Boolean).join(" ").toUpperCase(),
        telefone_lead: limparTelefone(d.telefone_lead),
        campanha: d.campanha || "manual",
        origem: "manual",
        status: "redirecionado",
        lead_manual: true,
        segmento: d.segmento || null,
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      setNovoLead(false);
      carregar();
    } catch (e) { alert("Erro ao adicionar lead: " + e.message); }
  }

  // Ações do admin nos leads (raro — quando o corretor não marcou)
  async function salvarAtendidoAdmin(leadId, { valor_proposta, temperatura }) {
    try {
      await supabase.from("plantao_leads").update({
        status: "atendido",
        atendido_em: new Date().toISOString(),
        valor_proposta: valor_proposta === "" || valor_proposta == null ? null : parseFloat(valor_proposta),
        temperatura: temperatura || "morno",
      }).eq("id", leadId);
      setMAtendido(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }
  async function salvarPerdidoAdmin(leadId, { motivo }) {
    try {
      await supabase.from("plantao_leads").update({ status: "perdido", motivo_perda: motivo || "Outro" }).eq("id", leadId);
      setMPerdido(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }
  async function salvarFechamentoAdmin(leadId, ficha) {
    try {
      const valorTotal = parseFloat(ficha.financeiro?.valor_total) || 0;
      await supabase.from("plantao_leads").update({
        status: "fechado", fechado_em: new Date().toISOString(),
        valor_fechado: valorTotal || null,
        pedido_info: ficha.pedido_info, moveis: ficha.moveis, financeiro: ficha.financeiro,
      }).eq("id", leadId);
      const lead = leads.find(l => l.id === leadId) || { id: leadId };
      const cor = corretores.find(c => c.id === lead.plantao_corretor_id);
      // Venda entra PENDENTE (sem Wix). O admin confirma depois no módulo Vendas.
      // Se o bloco admin do fechamento escolheu corretor, ficha.corretor_id manda; senão usa o do lead.
      const v = await criarVendaPendente({ ...lead, id: leadId }, ficha, ficha.corretor_id || cor?.corretor_id);
      if (v?.id) await supabase.from("plantao_leads").update({ venda_id: v.id }).eq("id", leadId);
      setMFechar(null);
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }
  async function marcarQuitadoAdmin(lead) {
    if (!window.confirm("Confirmar quitação do consórcio? Gera o nº do pedido Wix e libera pra fabricação.")) return;
    try {
      const ficha = { moveis: lead.moveis, pedido_info: lead.pedido_info, financeiro: lead.financeiro };
      const cor = corretores.find(c => c.id === lead.plantao_corretor_id);
      const w = await criarPedidoWix(lead, ficha, cor?.corretor_id, lead.venda_id);
      if (w && w.ok && w.numero) {
        await supabase.from("plantao_leads").update({ wix_numero: String(w.numero), consorcio_quitado: true }).eq("id", lead.id);
        carregar();
      } else { alert("Falha ao criar pedido Wix: " + (w && (w.erro || JSON.stringify(w.resposta)) || "erro")); }
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function excluirLead(lead) {
    if (!window.confirm(`Excluir o lead de "${lead.nome_lead || "sem nome"}"? A venda vinculada (se houver) também será removida. Ação não pode ser desfeita.`)) return;
    try {
      if (lead.venda_id) {
        await supabase.from("vendas").delete().eq("id", lead.venda_id);
      }
      const { error } = await supabase.from("plantao_leads").delete().eq("id", lead.id);
      if (error) throw error;
      carregar();
    } catch (e) { alert("Erro: " + e.message); }
  }

  async function excluir(id) {
    if (!window.confirm("Remover este corretor do plantão? O histórico de leads dele fica preservado.")) return;
    try {
      const { error } = await supabase.from("plantao_corretores").delete().eq("id", id);
      if (error) throw error;
      carregar();
    } catch (e) {
      alert("Erro: " + e.message);
    }
  }

  async function alternarAtivo(c) {
    try {
      const { error } = await supabase.from("plantao_corretores").update({ ativo: !c.ativo }).eq("id", c.id);
      if (error) throw error;
      carregar();
    } catch (e) {
      alert("Erro: " + e.message);
    }
  }

  // Leads filtrados
  const campanhasDisponiveis = useMemo(() => {
    const s = new Set(leads.map(l => l.campanha).filter(Boolean));
    return ["todos", ...Array.from(s)];
  }, [leads]);

  // Base do funil: período + corretor + campanha (SEM status/busca, pois os KPIs SÃO o funil)
  const leadsBase = useMemo(() => {
    let r = leads;
    if (fCorretor !== "todos") r = r.filter(l => l.plantao_corretor_id === fCorretor);
    if (fCampanha !== "todos") r = r.filter(l => l.campanha === fCampanha);
    if (fPeriodo !== "todos") {
      const agora = new Date();
      let inicio = null, fim = null;
      if (fPeriodo === "dia") {
        inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
      } else if (fPeriodo === "semana") {
        inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - agora.getDay());
      } else if (fPeriodo === "mes") {
        inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
      } else if (fPeriodo === "ano") {
        inicio = new Date(agora.getFullYear(), 0, 1);
      } else if (fPeriodo === "custom") {
        if (fDataDe)  inicio = new Date(fDataDe + "T00:00:00");
        if (fDataAte) fim    = new Date(fDataAte + "T23:59:59");
      }
      r = r.filter(l => {
        const dt = new Date(l.created_at);
        if (inicio && dt < inicio) return false;
        if (fim    && dt > fim)    return false;
        return true;
      });
    }
    return r;
  }, [leads, fCorretor, fCampanha, fPeriodo, fDataDe, fDataAte]);

  // Lista exibida = base + status + busca
  const leadsFiltrados = useMemo(() => {
    let r = leadsBase;
    if (fStatus !== "todos") r = r.filter(l => l.status === fStatus);
    if (fBusca) {
      const q = fBusca.toLowerCase();
      r = r.filter(l =>
        (l.nome_lead || "").toLowerCase().includes(q)
        || (l.telefone_lead || "").includes(q)
        || (l.nome_corretor || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [leadsBase, fStatus, fBusca]);

  function exportarCSV() {
    const linhas = [
      ["data","status","campanha","origem","lead_nome","lead_telefone","corretor_nome","corretor_telefone"],
      ...leadsFiltrados.map(l => [
        new Date(l.created_at).toLocaleString("pt-BR"),
        l.status || "",
        l.campanha || "",
        l.origem || "",
        l.nome_lead || "",
        l.telefone_lead || "",
        l.nome_corretor || "",
        l.telefone_corretor || "",
      ]),
    ];
    const csv = linhas.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plantao_leads_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const stats = useMemo(() => {
    const totalCorretores = corretores.length;
    const ativos = corretores.filter(c => c.ativo).length;
    const total = leadsBase.length;
    const trabalhados = leadsBase.filter(l => ["atendido", "fechado", "perdido"].includes(l.status)).length;
    const fechadosArr = leadsBase.filter(l => l.status === "fechado");
    const fechados = fechadosArr.length;
    const quentes = leadsBase.filter(l => l.temperatura === "quente" && !["fechado", "perdido"].includes(l.status)).length;
    const taxaAtend = total ? Math.round((trabalhados / total) * 100) : 0;
    const taxaConv = total ? Math.round((fechados / total) * 100) : 0;
    const pipeline = leadsBase.filter(l => l.status === "atendido").reduce((s, l) => s + (parseFloat(l.valor_proposta) || 0), 0);
    const valorFechado = fechadosArr.reduce((s, l) => s + (parseFloat(l.valor_fechado) || 0), 0);
    const ticket = fechados ? valorFechado / fechados : 0;
    return { totalCorretores, ativos, total, trabalhados, fechados, quentes, taxaAtend, taxaConv, pipeline, valorFechado, ticket };
  }, [corretores, leadsBase]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>
        Acesso restrito a administradores.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Plantão · WhatsApp Corretores"
        subtitle="Rodízio de leads da landing → WhatsApp dos corretores em plantão"
        icon={<Icon.rocket size={24} />}
        right={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Btn onClick={() => setNovoLead(true)} icon={<Icon.plus size={14} />}>Adicionar lead</Btn>
            <Btn variant="ghost" onClick={() => setNovo(true)} icon={<Icon.plus size={14} />}>Adicionar corretor</Btn>
          </div>
        }
      />

      <Note tone="info" icon={<Icon.info size={14} />}>
        <b>Parceria comercial livre.</b> Adesão ao plantão é voluntária. Corretor pode ativar/desativar seu plantão a qualquer momento sem penalidade. Rodízio distribui leads pra quem tiver disponível na janela de horário configurada.
      </Note>

      {/* KPIs do funil CRM */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10,
      }}>
        <KpiMini label="Total de leads" valor={stats.total} cor={T.brand} />
        <KpiMini label={`Atendidos · ${stats.taxaAtend}%`} valor={stats.trabalhados} cor={T.info} />
        <KpiMini label={`Fechados · ${stats.taxaConv}%`} valor={stats.fechados} cor={T.ok} />
        <KpiMini label="🔥 Quentes" valor={stats.quentes} cor={T.err} />
        <KpiMini label="Pipeline (propostas)" valor={fmt.brl(stats.pipeline)} cor={T.gold} />
        <KpiMini label="Fechado (R$)" valor={fmt.brl(stats.valorFechado)} cor={T.ok} />
        <KpiMini label="Ticket médio" valor={fmt.brl(stats.ticket)} cor={T.gold} />
        <KpiMini label="Corretores ativos" valor={`${stats.ativos}/${stats.totalCorretores}`} cor={T.brand} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
        {[
          { id: "leads",      label: `📩 Leads recebidos (${leads.length})` },
          { id: "corretores", label: `👥 Corretores (${corretores.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setAbaAtiva(t.id)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "10px 16px", fontFamily: "inherit",
            fontSize: 13, fontWeight: abaAtiva === t.id ? 700 : 500,
            color: abaAtiva === t.id ? T.brand : T.textSub,
            borderBottom: `2px solid ${abaAtiva === t.id ? T.brand : "transparent"}`,
          }}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", color: T.textDim, padding: 20 }}>Carregando…</div>}

      {abaAtiva === "corretores" && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {corretores.length === 0 && (
            <div style={{ textAlign: "center", color: T.textDim, padding: 30 }}>
              Nenhum corretor no plantão. Adicione o primeiro.
            </div>
          )}
          {corretores.map(c => (
            <CardCorretor key={c.id}
              corretor={c}
              corretoresCRM={corretoresCRM || []}
              onEditar={() => setEditando(c)}
              onExcluir={() => excluir(c.id)}
              onToggle={() => alternarAtivo(c)}
            />
          ))}
        </div>
      )}

      {abaAtiva === "leads" && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Barra de filtros */}
          <Card style={{ padding: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
              <Sel value={fCorretor} onChange={e => setFCorretor(e.target.value)}>
                <option value="todos">👥 Todos corretores</option>
                {corretores.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </Sel>
              <Sel value={fStatus} onChange={e => setFStatus(e.target.value)}>
                <option value="todos">📊 Todos status</option>
                <option value="redirecionado">🟡 Redirecionado</option>
                <option value="atendido">📞 Atendido</option>
                <option value="fechado">✅ Fechado</option>
                <option value="perdido">❌ Perdido</option>
                <option value="expirado">⏱ Expirado</option>
                <option value="fallback">⚠ Fallback</option>
              </Sel>
              <Sel value={fCampanha} onChange={e => setFCampanha(e.target.value)}>
                {campanhasDisponiveis.map(c => (
                  <option key={c} value={c}>{c === "todos" ? "🎯 Todas campanhas" : `🎯 ${c}`}</option>
                ))}
              </Sel>
              <Sel value={fPeriodo} onChange={e => setFPeriodo(e.target.value)}>
                <option value="todos">📅 Todo o período</option>
                <option value="dia">Hoje</option>
                <option value="semana">Esta semana</option>
                <option value="mes">Este mês</option>
                <option value="ano">Este ano</option>
                <option value="custom">Personalizado…</option>
              </Sel>
            </div>
            {fPeriodo === "custom" && (
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                <label style={{ fontSize: 11, color: T.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                  De:
                  <input type="date" value={fDataDe} onChange={e => setFDataDe(e.target.value)}
                    style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "6px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                </label>
                <label style={{ fontSize: 11, color: T.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                  Até:
                  <input type="date" value={fDataAte} onChange={e => setFDataAte(e.target.value)}
                    style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "6px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                </label>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <Input placeholder="🔍 Buscar por nome/telefone do lead ou corretor..." value={fBusca} onChange={e => setFBusca(e.target.value)} />
              </div>
              <Btn variant="ghost" onClick={exportarCSV} icon={<Icon.arrow size={12} />}>
                Exportar CSV ({leadsFiltrados.length})
              </Btn>
            </div>
          </Card>

          {leadsFiltrados.length === 0 && (
            <div style={{ textAlign: "center", color: T.textDim, padding: 30 }}>
              Nenhum lead corresponde aos filtros.
            </div>
          )}
          {leadsFiltrados.map(l => <CardLead key={l.id} lead={l} onAtendido={setMAtendido} onPerdido={setMPerdido} onFechar={setMFechar} onVerProjeto={setMProjeto} onQuitar={marcarQuitadoAdmin} onExcluir={excluirLead} />)}
        </div>
      )}

      {(novo || editando) && (
        <FormPlantao
          corretor={editando || { nome: "", telefone: "", ativo: true, horarios: HORARIO_COMERCIAL, ordem: (corretores.length || 0) + 1, observacoes: "", corretor_id: null }}
          corretoresCRM={corretoresCRM || []}
          onSalvar={salvar}
          onCancelar={() => { setNovo(false); setEditando(null); }}
        />
      )}

      {novoLead && (
        <LeadForm corretores={corretores} onSalvar={salvarLead} onCancelar={() => setNovoLead(false)} />
      )}
      {mAtendido && (
        <ModalAtendido lead={mAtendido} onSalvar={salvarAtendidoAdmin} onCancelar={() => setMAtendido(null)} />
      )}
      {mPerdido && (
        <ModalPerdido lead={mPerdido} onSalvar={salvarPerdidoAdmin} onCancelar={() => setMPerdido(null)} />
      )}
      {mProjeto && (
        <ModalProjeto
          lead={mProjeto}
          onEditar={() => { const l = mProjeto; setMProjeto(null); setMFechar(l); }}
          onClose={() => setMProjeto(null)}
        />
      )}
      {mFechar && (() => {
        const corLead = corretores.find(c => c.id === mFechar.plantao_corretor_id);
        return (
          <ModalFechamento
            lead={{ ...mFechar, corretor_id: corLead?.corretor_id || "" }}
            onSalvar={salvarFechamentoAdmin}
            onCancelar={() => setMFechar(null)}
            corretores={corretoresCRM || []}
            isAdmin
            currentUser={currentUser}
          />
        );
      })()}
    </div>
  );
}

function LeadForm({ corretores, onSalvar, onCancelar }) {
  const lista = corretores || [];
  const ativos = lista.filter(c => c.ativo !== false);
  const [d, setD] = useState({
    nome_lead: "", sobrenome_lead: "", telefone_lead: "",
    plantao_corretor_id: (ativos[0] && ativos[0].id) || (lista[0] && lista[0].id) || "",
    campanha: "manual", segmento: "",
  });
  const podeSalvar = d.nome_lead.trim() && d.plantao_corretor_id;
  const labelStyle = { fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 };
  const selStyle = { width: "100%", padding: "10px 12px", background: T.input, border: `1px solid ${T.borderMid}`, borderRadius: T.r.md, color: T.text, fontSize: 13, boxSizing: "border-box" };
  return (
    <Modal title="Adicionar lead manual" onClose={onCancelar} width={520}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Nome" value={d.nome_lead} onChange={e => setD({ ...d, nome_lead: e.target.value.toUpperCase() })} />
        <Input label="Sobrenome" value={d.sobrenome_lead} onChange={e => setD({ ...d, sobrenome_lead: e.target.value.toUpperCase() })} />
        <Input label="WhatsApp / telefone" value={d.telefone_lead} onChange={e => setD({ ...d, telefone_lead: e.target.value })} placeholder="85999999999" />
      </div>
      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <div style={labelStyle}>Corretor responsável</div>
          <Sel value={d.plantao_corretor_id} onChange={e => setD({ ...d, plantao_corretor_id: e.target.value })} style={selStyle}>
            {lista.map(c => <option key={c.id} value={c.id}>{c.nome}{c.ativo === false ? " (inativo)" : ""}</option>)}
          </Sel>
        </div>
        <div>
          <div style={labelStyle}>Segmento</div>
          <Sel value={d.segmento} onChange={e => setD({ ...d, segmento: e.target.value })} style={selStyle}>
            <option value="">—</option>
            <option value="salao">Salão de beleza</option>
            <option value="barbearia">Barbearia</option>
            <option value="clinica">Clínica de estética</option>
            <option value="outro">Outro</option>
          </Sel>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <Input label="Campanha / origem" value={d.campanha} onChange={e => setD({ ...d, campanha: e.target.value })} placeholder="Ex: indicação, feira, walk-in" />
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={() => podeSalvar && onSalvar(d)} disabled={!podeSalvar} icon={<Icon.check size={14} />}>Adicionar lead</Btn>
      </div>
    </Modal>
  );
}

function KpiMini({ label, valor, cor }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${cor}`, borderRadius: T.r.md, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{valor}</div>
    </div>
  );
}

function CardCorretor({ corretor, onEditar, onExcluir, onToggle, corretoresCRM }) {
  const diasAtivos = new Set((corretor.horarios || []).map(h => h.dia));
  const ultimo = corretor.ultimo_atendido && corretor.ultimo_atendido !== "1970-01-01T00:00:00+00:00"
    ? new Date(corretor.ultimo_atendido).toLocaleString("pt-BR")
    : "—";
  return (
    <Card style={{
      padding: 16,
      borderLeft: `3px solid ${corretor.ativo ? T.ok : T.textDim}`,
      opacity: corretor.ativo ? 1 : 0.6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{corretor.nome}</div>
            <span style={{
              padding: "2px 8px", borderRadius: 100,
              background: corretor.ativo ? T.ok + "22" : T.textDim + "22",
              color: corretor.ativo ? T.ok : T.textDim,
              fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
            }}>
              {corretor.ativo ? "🟢 Ativo" : "⏸ Pausado"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>
            📱 {formatarTelefone(corretor.telefone)}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            {DIAS_SEMANA.map(d => (
              <span key={d.id} title={d.label} style={{
                width: 22, height: 22, borderRadius: 4,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 700,
                background: diasAtivos.has(d.id) ? T.brand + "33" : T.input,
                color: diasAtivos.has(d.id) ? T.brand : T.textDim,
                border: `1px solid ${diasAtivos.has(d.id) ? T.brand + "66" : T.border}`,
              }}>{d.curto}</span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap", fontSize: 10, color: T.textDim }}>
            <span>📊 {corretor.total_leads || 0} leads total</span>
            <span>· Último: {ultimo}</span>
            {corretor.max_leads_dia != null && (
              <span style={{ color: T.warn }}>· ⚡ Máx {corretor.max_leads_dia}/dia</span>
            )}
            {corretor.corretor_id ? (() => {
              const vinculado = (corretoresCRM || []).find(cc => cc._uuid === corretor.corretor_id);
              return <span style={{ color: T.ok }}>· 🔗 Vinculado{vinculado ? `: ${vinculado.nome}` : ""}</span>;
            })() : (
              <span style={{ color: T.textDim, fontStyle: "italic" }}>· ⚠ Sem vínculo CRM</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Btn variant="ghost" onClick={onToggle} style={{ fontSize: 11 }}>
            {corretor.ativo ? "⏸ Pausar" : "▶ Ativar"}
          </Btn>
          <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={onEditar}>Editar</Btn>
          <Btn variant="danger" icon={<Icon.trash size={12} />} onClick={onExcluir}>Remover</Btn>
        </div>
      </div>
    </Card>
  );
}

function CardLead({ lead, onAtendido, onPerdido, onFechar, onVerProjeto, onQuitar, onExcluir }) {
  const dt = new Date(lead.created_at).toLocaleString("pt-BR");
  const statusInfo = {
    redirecionado: { cor: T.brand,   label: "Redirecionado" },
    fallback:      { cor: T.warn,    label: "Fallback comercial" },
    atendido:      { cor: T.info,    label: "Atendido" },
    fechado:       { cor: T.ok,      label: "Fechado" },
    perdido:       { cor: T.err,     label: "Perdido" },
    expirado:      { cor: T.textDim, label: "Expirado" },
  }[lead.status] || { cor: T.textDim, label: lead.status };
  const telLead = lead.telefone_lead ? String(lead.telefone_lead).replace(/\D/g, "") : "";
  const waHref = telLead ? `https://wa.me/${telLead.length === 10 || telLead.length === 11 ? "55" + telLead : telLead}` : null;
  return (
    <Card style={{ padding: 14, borderLeft: `3px solid ${statusInfo.cor}` }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Header status + campanha + data */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {lead.status === "fechado" && lead.wix_numero && (
            <span style={{ fontSize: 11, color: T.gold, fontWeight: 700 }}>Nº {lead.wix_numero}</span>
          )}
          <span style={{
            padding: "2px 8px", borderRadius: 100,
            background: statusInfo.cor + "22", color: statusInfo.cor,
            fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
          }}>{statusInfo.label}</span>
          {lead.status === "fechado" && lead.financeiro?.forma_pagamento === "consorcio" && !lead.wix_numero && (
            <span style={{ padding: "2px 8px", borderRadius: 100, background: T.brand + "22", color: T.brand, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>📅 Consórcio</span>
          )}
          {lead.campanha && (
            <span style={{
              padding: "2px 8px", borderRadius: 100,
              background: T.gold + "22", color: T.gold,
              fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
            }}>🎯 {lead.campanha}</span>
          )}
          {lead.origem && <span style={{ fontSize: 10, color: T.textDim }}>📍 {lead.origem}</span>}
          <span style={{ fontSize: 10, color: T.textDim, marginLeft: "auto" }}>{dt}</span>
          {onExcluir && (
            <button onClick={() => onExcluir(lead)} title="Excluir lead" style={{ background: "none", border: "none", cursor: "pointer", color: T.err, fontSize: 13 }}>🗑️</button>
          )}
        </div>

        {/* Cliente (LEAD) */}
        <div style={{ background: T.input, borderRadius: T.r.md, padding: "8px 10px", border: `1px dashed ${T.brand}44` }}>
          <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600, marginBottom: 2 }}>
            👤 Cliente
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{lead.nome_lead || "— sem nome —"}</div>
            {waHref ? (
              <a href={waHref} target="_blank" rel="noopener noreferrer" style={{
                fontSize: 12, color: "#25D366", textDecoration: "underline", fontWeight: 600,
              }}>💬 {formatarTelefone(telLead.length === 10 || telLead.length === 11 ? "55" + telLead : telLead)}</a>
            ) : (
              <span style={{ fontSize: 12, color: T.textDim, fontStyle: "italic" }}>sem telefone</span>
            )}
          </div>
        </div>

        {/* Corretor sorteado */}
        <div style={{ fontSize: 10, color: T.textSub, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span>➡️ Encaminhado para <b style={{ color: T.text }}>{lead.nome_corretor || "—"}</b></span>
          <span>· 📱 {formatarTelefone(lead.telefone_corretor)}</span>
        </div>

        {/* Info do funil */}
        {(lead.valor_proposta != null || lead.valor_fechado != null || lead.temperatura || lead.wix_numero || lead.motivo_perda) && (
          <div style={{ fontSize: 11, display: "flex", gap: 12, flexWrap: "wrap" }}>
            {lead.valor_proposta != null && <span style={{ color: T.gold }}>📄 Proposta {fmt.brl(lead.valor_proposta)}</span>}
            {lead.valor_fechado != null && <span style={{ color: T.ok }}>✅ Fechado {fmt.brl(lead.valor_fechado)}</span>}
            {lead.temperatura && <span style={{ color: T.textSub }}>{lead.temperatura === "quente" ? "🔥 Quente" : lead.temperatura === "frio" ? "❄️ Frio" : "🌡️ Morno"}</span>}
            {lead.status === "perdido" && lead.motivo_perda && <span style={{ color: T.err }}>Motivo: {lead.motivo_perda}</span>}
          </div>
        )}

        {/* Ações do admin (raro — quando o corretor não marcou) */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {lead.status === "redirecionado" && (
            <>
              {onAtendido && <Btn variant="ghost" onClick={() => onAtendido(lead)} style={{ fontSize: 10 }}>📞 Atendido</Btn>}
              {onPerdido && <Btn variant="ghost" onClick={() => onPerdido(lead)} style={{ fontSize: 10, color: T.err }}>❌ Perdido</Btn>}
            </>
          )}
          {lead.status === "atendido" && (
            <>
              {onAtendido && <Btn variant="ghost" onClick={() => onAtendido(lead)} style={{ fontSize: 10 }}>✏️ Proposta</Btn>}
              {onFechar && <Btn variant="success" onClick={() => onFechar(lead)} style={{ fontSize: 10 }}>✅ Fechar</Btn>}
              {onPerdido && <Btn variant="ghost" onClick={() => onPerdido(lead)} style={{ fontSize: 10, color: T.err }}>❌ Perdido</Btn>}
            </>
          )}
          {lead.status === "fechado" && onVerProjeto && (
            <Btn variant="ghost" onClick={() => onVerProjeto(lead)} style={{ fontSize: 10 }}>📋 Ver projeto</Btn>
          )}
        </div>
      </div>
    </Card>
  );
}

function FormPlantao({ corretor, corretoresCRM, onSalvar, onCancelar }) {
  const [c, setC] = useState({
    ...corretor,
    horarios: corretor.horarios && corretor.horarios.length ? corretor.horarios : [],
  });

  function toggleDia(diaId) {
    const existente = c.horarios.find(h => h.dia === diaId);
    if (existente) {
      setC({ ...c, horarios: c.horarios.filter(h => h.dia !== diaId) });
    } else {
      const defaultInicio = diaId === 6 ? "09:00" : "08:00";
      const defaultFim    = diaId === 6 ? "13:00" : "18:00";
      setC({ ...c, horarios: [...c.horarios, { dia: diaId, inicio: defaultInicio, fim: defaultFim }] });
    }
  }

  function editarHorario(diaId, campo, valor) {
    setC({
      ...c,
      horarios: c.horarios.map(h => h.dia === diaId ? { ...h, [campo]: valor } : h),
    });
  }

  return (
    <Modal title={corretor.id ? "Editar corretor no plantão" : "Adicionar corretor ao plantão"} onClose={onCancelar} width={640}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Nome" value={c.nome} onChange={e => setC({ ...c, nome: e.target.value })} />
        <Input label="Telefone (com DDD)" value={c.telefone} onChange={e => setC({ ...c, telefone: e.target.value })} placeholder="85999999999" />
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: T.textSub, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
          Horário de atendimento
        </div>
        <div style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: 14 }}>
          {DIAS_SEMANA.map(d => {
            const h = c.horarios.find(x => x.dia === d.id);
            const ativo = !!h;
            return (
              <div key={d.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                borderBottom: d.id < 6 ? `1px dashed ${T.border}` : "none",
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 80, cursor: "pointer" }}>
                  <input type="checkbox" checked={ativo} onChange={() => toggleDia(d.id)} style={{ accentColor: T.brand, cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: T.text, fontWeight: ativo ? 600 : 400 }}>{d.label}</span>
                </label>
                {ativo && (
                  <>
                    <input type="time" value={h.inicio} onChange={e => editarHorario(d.id, "inicio", e.target.value)}
                      style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "4px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                    <span style={{ color: T.textDim, fontSize: 11 }}>até</span>
                    <input type="time" value={h.fim} onChange={e => editarHorario(d.id, "fim", e.target.value)}
                      style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "4px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
                  </>
                )}
                {!ativo && <span style={{ color: T.textDim, fontSize: 11, fontStyle: "italic" }}>Não atende</span>}
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: T.textDim, marginTop: 6 }}>
          Horário local de Fortaleza (America/Fortaleza). Fora dessas janelas o corretor não recebe leads.
        </div>
      </div>

      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Ordem (menor = prioridade)" type="number" value={c.ordem} onChange={e => setC({ ...c, ordem: parseInt(e.target.value, 10) || 0 })} />
        <Input
          label="Máx. leads por dia (opcional, vazio = ilimitado)"
          type="number"
          value={c.max_leads_dia == null ? "" : c.max_leads_dia}
          onChange={e => setC({ ...c, max_leads_dia: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
          placeholder="Ex: 2 para baixa frequência"
        />
      </div>
      <div style={{ marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input type="checkbox" checked={c.ativo} onChange={e => setC({ ...c, ativo: e.target.checked })} style={{ accentColor: T.brand, cursor: "pointer" }} />
          <span style={{ fontSize: 13, color: T.text }}>Ativo no plantão</span>
        </label>
      </div>

      <div style={{ marginTop: 12 }}>
        <Input label="Observações (opcional)" value={c.observacoes || ""} onChange={e => setC({ ...c, observacoes: e.target.value })} />
      </div>

      <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={() => onSalvar(c)} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}
